import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ShoppingBag, ArrowRight, Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import TextMarquee from '../components/TextMarquee';
import { usePageImages } from '../hooks/usePageImages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewShop = () => {
  const { language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '' });
  const { getImage, getImages } = usePageImages('studio');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!selectedProduct || !customerInfo.name || !customerInfo.email) return;

    setCheckoutLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/checkout/create-session?product_slug=${selectedProduct.slug}&customer_email=${encodeURIComponent(customerInfo.email)}&customer_name=${encodeURIComponent(customerInfo.name)}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        toast.error(language === 'en' ? 'Error creating checkout session' : 'Error al crear la sesión de pago');
      }
    } catch (error) {
      toast.error(language === 'en' ? 'Error processing checkout' : 'Error al procesar el pago');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const content = {
    en: {
      overline: "The Studio",
      title: "Digital Tools for Founders Who Build",
      subtitle: "Systems, templates, and playbooks designed for experience designers, operators, and creative directors.",
      outcome: "What You'll Achieve",
      whatsInside: "What's Inside",
      whoFor: "Who It's For",
      format: "Format",
      buyNow: "Buy Now",
      loading: "Loading...",
      noProducts: "No products available yet.",
      checkout: "Checkout",
      yourName: "Your Name",
      yourEmail: "Your Email",
      proceedToPayment: "Proceed to Payment",
      cancel: "Cancel",
      marqueeText: ["Systems.", "Templates.", "Playbooks.", "Tools."]
    },
    es: {
      overline: "El Studio",
      title: "Herramientas Digitales Para Fundadores Que Construyen",
      subtitle: "Sistemas, plantillas y playbooks diseñados para diseñadores de experiencias, operadores y directores creativos.",
      outcome: "Lo Que Lograrás",
      whatsInside: "Qué Incluye",
      whoFor: "Para Quién Es",
      format: "Formato",
      buyNow: "Comprar",
      loading: "Cargando...",
      noProducts: "Aún no hay productos disponibles.",
      checkout: "Pagar",
      yourName: "Tu Nombre",
      yourEmail: "Tu Email",
      proceedToPayment: "Proceder al Pago",
      cancel: "Cancelar",
      marqueeText: ["Sistemas.", "Plantillas.", "Playbooks.", "Herramientas."]
    }
  };

  const t = content[language];

  if (loading) {
    return (
      <div className="min-h-screen bg-vainilla flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-terracota" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vainilla" data-testid="shop-page">
      {/* Hero Section - Editorial Style */}
      <section className="pt-32 pb-20 px-6 bg-stone-900 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-terracota text-sm font-bold tracking-[0.3em] uppercase mb-6">
            {t.overline}
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.95] mb-6">
            {t.title}
          </h1>
          <p className="text-xl md:text-2xl text-stone-400 max-w-3xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Scrolling Marquee */}
      <TextMarquee 
        lines={t.marqueeText}
        speed={25}
        dark={false}
      />

      {/* Products Grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          {products.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 text-xl">{t.noProducts}</p>
            </div>
          ) : (
            <div className="space-y-24">
              {products.map((product, index) => (
                <div 
                  key={product.id}
                  className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center`}
                  data-testid={`product-${product.slug}`}
                >
                  {/* Product Image */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : 'lg:order-1'}>
                    <div className={`aspect-[4/3] rounded-2xl overflow-hidden ${index % 2 === 0 ? 'bg-champagne' : 'bg-stone-100'}`}>
                      {product.featured_image ? (
                        <img 
                          src={product.featured_image} 
                          alt={language === 'en' ? product.title : (product.title_es || product.title)}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="w-24 h-24 text-terracota/20" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : 'lg:order-2'}>
                    <span className="inline-block text-terracota text-sm font-bold tracking-[0.2em] uppercase mb-4 px-4 py-2 bg-champagne rounded-full">
                      {language === 'en' ? product.category : (product.category_es || product.category)}
                    </span>
                    
                    <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-4">
                      {language === 'en' ? product.title : (product.title_es || product.title)}
                    </h2>
                    
                    {/* Outcome */}
                    <div className="mb-6">
                      <h3 className="text-xs font-bold text-stone-400 uppercase tracking-[0.15em] mb-2">
                        {t.outcome}
                      </h3>
                      <p className="text-lg text-stone-700 leading-relaxed">
                        {language === 'en' ? product.outcome : (product.outcome_es || product.outcome)}
                      </p>
                    </div>

                    {/* What's Inside */}
                    <div className="mb-6">
                      <h3 className="text-xs font-bold text-stone-400 uppercase tracking-[0.15em] mb-3">
                        {t.whatsInside}
                      </h3>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(language === 'en' ? product.whats_inside : (product.whats_inside_es || product.whats_inside))?.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-stone-600">
                            <Check className="w-4 h-4 text-terracota mt-1 flex-shrink-0" />
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Who It's For & Format */}
                    <div className="grid grid-cols-2 gap-6 mb-8">
                      <div>
                        <h3 className="text-xs font-bold text-stone-400 uppercase tracking-[0.15em] mb-1">
                          {t.whoFor}
                        </h3>
                        <p className="text-sm text-stone-600">
                          {language === 'en' ? product.who_its_for : (product.who_its_for_es || product.who_its_for)}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-stone-400 uppercase tracking-[0.15em] mb-1">
                          {t.format}
                        </h3>
                        <p className="text-sm text-stone-600">
                          {language === 'en' ? product.format : (product.format_es || product.format)}
                        </p>
                      </div>
                    </div>

                    {/* Price & CTA */}
                    <div className="flex items-center gap-6 pt-6 border-t border-stone-200">
                      <span className="font-serif text-4xl md:text-5xl font-bold text-stone-900">
                        ${product.price.toFixed(0)}
                      </span>
                      <button
                        onClick={() => setSelectedProduct(product)}
                        className="flex items-center gap-3 bg-terracota text-white px-8 py-4 rounded-full font-semibold hover:bg-stone-900 transition-all duration-300"
                        data-testid={`buy-${product.slug}`}
                      >
                        {t.buyNow}
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
            {language === 'es' ? '¿Tienes preguntas?' : 'Have Questions?'}
          </h2>
          <p className="text-xl text-stone-400 mb-10">
            {language === 'es' 
              ? 'Agenda una llamada para discutir qué herramienta es adecuada para ti.'
              : "Schedule a call to discuss which tool is right for you."}
          </p>
          <Link to="/contact">
            <button className="bg-terracota text-white px-10 py-5 text-lg font-semibold rounded-full hover:bg-white hover:text-stone-900 transition-all duration-300 flex items-center gap-3 mx-auto">
              {language === 'es' ? 'Contactar' : 'Get In Touch'}
              <ArrowRight className="w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>

      {/* Checkout Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-stone-200 flex items-center justify-between">
              <h3 className="font-serif text-2xl text-stone-900">{t.checkout}</h3>
              <button 
                onClick={() => setSelectedProduct(null)}
                className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:bg-stone-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCheckout} className="p-6 space-y-5">
              <div className="bg-champagne rounded-xl p-5">
                <p className="font-serif text-xl font-semibold text-stone-900">
                  {language === 'en' ? selectedProduct.title : (selectedProduct.title_es || selectedProduct.title)}
                </p>
                <p className="text-3xl font-bold text-terracota mt-2">
                  ${selectedProduct.price.toFixed(2)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">
                  {t.yourName}
                </label>
                <input
                  type="text"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                  className="w-full border border-stone-300 rounded-xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-terracota/50 focus:border-terracota transition-all"
                  required
                  data-testid="checkout-name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">
                  {t.yourEmail}
                </label>
                <input
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  className="w-full border border-stone-300 rounded-xl px-4 py-4 focus:outline-none focus:ring-2 focus:ring-terracota/50 focus:border-terracota transition-all"
                  required
                  data-testid="checkout-email"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setSelectedProduct(null)}
                  className="flex-1 px-4 py-4 border border-stone-300 rounded-xl text-stone-700 font-semibold hover:bg-stone-50 transition-colors"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={checkoutLoading}
                  className="flex-1 px-4 py-4 bg-terracota text-white rounded-xl font-semibold hover:bg-stone-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="checkout-submit"
                >
                  {checkoutLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t.proceedToPayment
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewShop;
